import { cva, type VariantProps } from 'class-variance-authority';
import { type HTMLAttributes } from 'react';

import { cn } from '../../lib/cn';

/**
 * The official Gestion Sani-plus lockup, inlined.
 *
 * The client's `brand/gestion-saniplus-logo.svg` is a 576 x 128.52 artboard:
 * the circular mark, `SANIPLUS` at display size and `GESTION` above it. Its
 * paths are inlined here as JSX rather than shipped as an asset, so the mark
 * needs no bundler rule, no fetch, no cache entry and no flash of missing logo
 * on a worker's first cold, offline start in a plant basement.
 *
 * **Two things were changed on the way in, both deliberate:**
 *
 * 1. **The white disc is not drawn.** In the source it is a base fill sitting
 *    under the two coloured shapes, which together cover it. Keeping it would
 *    have meant painting an opaque light circle on the night chassis — so the
 *    mark is transparent behind, and reads on any surface.
 * 2. **The forty-odd zero-area slivers** Illustrator left around the circle's
 *    edge are dropped. They render nothing and cost 3 kB.
 *
 * The colouring follows `brand/BRAND.md` exactly: **SANI** and the mark's S in
 * slate, **PLUS** and **GESTION** in brand blue, and the two halves are never
 * recoloured independently.
 *
 * Brand blue is 2.65:1 on white, and this is one of the exactly three places
 * DESIGN.md permits it anyway: a wordmark is identity, not information, and
 * removing the blue removes no meaning. The slate half is `currentColor`
 * rather than a fixed `#4C5153`, so it stays legible on the night rail as well
 * as on white — `tone` picks the ink.
 */

const wordmark = cva('inline-flex select-none flex-col items-start leading-none', {
  variants: {
    size: {
      /** The worker header rail and the admin sidebar. */
      sm: '',
      /** The login screen. */
      md: '',
      /** A splash or a print header. */
      lg: '',
    },
    tone: {
      /** On a light surface. */
      default: 'text-ink-subtle',
      /**
       * On the slate chassis — the worker rail, the admin sidebar.
       * `--color-ink-inverse` is `slate-1000` on the night theme (1.04:1 on
       * the chassis); `-inverse-muted` measures 7.16:1 and is the one that
       * survives the theme swap.
       */
      inverse: 'text-ink-inverse-muted',
    },
  },
  defaultVariants: { size: 'sm', tone: 'default' },
});

/** The lockup's drawn height per size. The artboard is 576 x 128.52. */
const MARK_HEIGHT = { sm: 'h-5', md: 'h-6', lg: 'h-8' } as const;

export type WordmarkVariants = VariantProps<typeof wordmark>;

export interface WordmarkProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'>,
    WordmarkVariants {
  /**
   * The accessible name — "Gestion Saniplus". Required: a logo with no name is
   * an unlabelled image to a screen reader.
   */
  readonly label: string;
  /**
   * An external asset, for a tenant that supplies its own. Overrides
   * everything below.
   */
  readonly src?: string;
  /**
   * `lockup` (the default) draws the real mark. `text` is the type stand-in
   * the package shipped before the asset arrived; it is kept for a surface
   * where the mark is too small to read, and for anyone who wants the
   * wordmark without the circle.
   */
  readonly variant?: 'lockup' | 'text';
  /** Product sub-lockup, e.g. "Connect". Rendered in muted ink beneath. */
  readonly product?: string;
}

export function Wordmark({
  label,
  src,
  variant = 'lockup',
  product,
  size,
  tone,
  className,
  ...rest
}: WordmarkProps) {
  const height = MARK_HEIGHT[size ?? 'sm'];

  if (src !== undefined) {
    return (
      <span {...rest} className={cn(wordmark({ size, tone }), className)}>
        <img src={src} alt={label} className={cn(height, 'w-auto')} />
      </span>
    );
  }

  if (variant === 'lockup') {
    return (
      <span {...rest} className={cn(wordmark({ size, tone }), className)}>
        <LockupSvg label={label} className={cn(height, 'w-auto')} />
        {product === undefined ? null : (
          <span aria-hidden="true" className="mt-0.5 text-label uppercase text-ink-inverse-muted">
            {product}
          </span>
        )}
      </span>
    );
  }

  const gestion = size === 'sm' || size === undefined ? 'text-chip' : 'text-label';
  const name = size === 'sm' || size === undefined ? 'text-title-2' : 'text-headline';

  return (
    <span
      {...rest}
      role="img"
      aria-label={label}
      className={cn(wordmark({ size, tone }), className)}
    >
      <span aria-hidden="true" className={cn(gestion, 'uppercase text-brand')}>
        {'GESTION'}
      </span>
      <span aria-hidden="true" className={cn(name, 'mt-0.5 uppercase tracking-readout')}>
        <span className="text-ink-subtle">{'SANI'}</span>
        <span className="text-brand">{'PLUS'}</span>
      </span>
      {product !== undefined ? (
        <span aria-hidden="true" className="mt-0.5 text-label uppercase text-ink-inverse-muted">
          {product}
        </span>
      ) : null}
    </span>
  );
}

function LockupSvg({ label, className }: { readonly label: string; readonly className: string }) {
  return (
    <svg
      viewBox="0 0 576 128.52"
      role="img"
      aria-label={label}
      focusable="false"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="fill-current"
        d="M77.54,98.89c0-7.26-5.28-13.64-15.83-19.12l-24.04-12.37c-10.57-5.35-15.85-13.7-15.85-25.07s3.83-19.72,11.51-25.48c6.71-5.07,15.35-7.6,25.9-7.6h38.16C84.09,1.23,67.85-2.02,51.42,1.34,16.66,8.43-5.76,42.36,1.33,77.12c2.95,14.46,10.55,26.8,20.92,35.75H59.24c12.2,0,18.3-4.66,18.3-13.99Z"
      />
      <path
        className="fill-current"
        d="M147.07,103.36v-6.76h39.8c3.81,0,5.72-1.41,5.72-4.23v-5.79c0-2.82-1.91-4.23-5.72-4.23h-26.92c-2.6,0-4.81-.27-6.63-.81-1.82-.54-3.31-1.3-4.45-2.28-1.15-.97-1.98-2.13-2.5-3.48-.52-1.34-.78-2.84-.78-4.49v-2.67c0-1.65,.26-3.14,.78-4.49,.52-1.34,1.35-2.5,2.5-3.48,1.15-.97,2.63-1.73,4.45-2.28,1.82-.54,4.03-.81,6.63-.81h38.76v6.76h-38.76c-3.82,0-5.72,1.41-5.72,4.23v2.8c0,2.82,1.91,4.23,5.72,4.23h26.92c2.6,0,4.81,.27,6.63,.81,1.82,.54,3.3,1.3,4.45,2.28,1.15,.98,1.98,2.14,2.5,3.48,.52,1.34,.78,2.84,.78,4.49v5.66c0,1.65-.26,3.14-.78,4.49-.52,1.34-1.35,2.5-2.5,3.48-1.15,.98-2.63,1.73-4.45,2.28-1.82,.54-4.03,.81-6.63,.81h-39.8Z"
      />
      <path
        className="fill-current"
        d="M257.1,103.36l-23.61-36.87-24,36.87h-9.62l30.43-45.78h6.31l30.24,45.78h-9.75Z"
      />
      <path
        className="fill-current"
        d="M325.51,103.36l-48.84-35.64v35.64h-7.54V57.58h7.48l45.59,34.01V57.58h7.54v45.78h-4.23Z"
      />
      <path
        className="fill-current"
        d="M336.56,103.36V57.58h8.65v45.78h-8.65Z"
      />
      <path
        className="fill-brand"
        d="M127.21,51.43c-.11-.52-.22-1.05-.34-1.57-.03-.13-.06-.26-.09-.39-.09-.39-.18-.78-.28-1.16-.04-.15-.08-.31-.12-.46-.1-.36-.19-.72-.29-1.08-.05-.16-.09-.32-.13-.47-.1-.35-.21-.7-.32-1.05-.05-.16-.1-.31-.15-.47-.11-.35-.23-.7-.34-1.05-.05-.15-.1-.3-.15-.45-.12-.35-.24-.71-.37-1.06-.05-.14-.1-.27-.15-.41-.14-.37-.28-.73-.42-1.1-.05-.11-.09-.23-.13-.35-.16-.4-.33-.79-.49-1.19-.03-.08-.07-.16-.1-.24-.2-.47-.4-.93-.62-1.39h0c-.66-1.46-1.37-2.87-2.13-4.25,0-.02-.02-.04-.03-.06-.23-.43-.47-.85-.72-1.27-.04-.06-.08-.13-.12-.2-.22-.38-.44-.75-.67-1.12-.05-.09-.1-.17-.15-.25-.22-.35-.44-.7-.66-1.04-.06-.09-.12-.18-.18-.27-.22-.34-.45-.68-.68-1.01-.06-.09-.12-.18-.18-.27-.23-.34-.47-.67-.71-1.01-.05-.08-.11-.16-.17-.24-.25-.35-.51-.7-.77-1.04-.04-.06-.09-.12-.13-.17-.29-.39-.59-.77-.9-1.15,0-.01-.02-.02-.03-.04-1.28-1.6-2.64-3.14-4.06-4.61-.05-.05-.1-.1-.15-.15-.1-.1-.2-.2-.29-.3h-43.93c-12.34,0-18.5,4.66-18.5,13.98,0,7.4,5.35,13.78,16.03,19.12l23.23,11.52c11.1,5.62,16.66,14.05,16.66,25.29s-3.91,19.46-11.72,25.5c-6.86,5.48-15.42,8.22-25.7,8.22h-28.39c.08,.04,.16,.07,.24,.11,.27,.12,.55,.24,.83,.36,.18,.08,.36,.15,.54,.23,.28,.12,.56,.24,.84,.35,.18,.07,.36,.14,.54,.21,.28,.12,.57,.22,.85,.34,.18,.06,.36,.13,.54,.2,.29,.11,.58,.21,.87,.32,.18,.06,.35,.12,.53,.18,.3,.1,.59,.2,.89,.3,.17,.05,.34,.11,.51,.16,.31,.1,.61,.2,.92,.29,.16,.05,.32,.09,.48,.14,.32,.09,.65,.2,.97,.28,.13,.03,.26,.06,.39,.1,.36,.09,.71,.2,1.07,.28h.01c1.29,.32,2.6,.61,3.91,.85,.15,.03,.29,.05,.43,.08,.31,.06,.62,.11,.93,.15,.19,.04,.39,.07,.58,.09,.29,.04,.59,.08,.88,.13,.21,.03,.43,.06,.64,.09,.28,.03,.57,.07,.85,.1,.23,.03,.45,.05,.68,.07,.27,.03,.55,.06,.83,.08,.24,.02,.47,.04,.7,.06,.27,.02,.54,.04,.82,.06l.73,.05c.27,.02,.53,.03,.8,.04,.25,0,.5,.02,.75,.03,.26,0,.53,.02,.79,.03,.25,0,.51,0,.77,0,.26,0,.52,0,.78,0,.26,0,.52,0,.78,0,.26,0,.51-.01,.77-.02,.27,0,.53-.02,.8-.03,.25-.01,.51-.02,.76-.03,.27-.02,.54-.03,.82-.05,.25-.02,.5-.03,.75-.05,.28-.02,.56-.05,.85-.07,.24-.03,.48-.04,.72-.07,.29-.03,.59-.06,.88-.1,.23-.03,.47-.05,.7-.08,.31-.04,.62-.08,.93-.13,.22-.03,.43-.06,.65-.09,.35-.05,.69-.12,1.04-.17,.18-.03,.36-.06,.54-.09,.53-.09,1.05-.19,1.58-.3,34.77-7.09,57.2-41.02,50.11-75.78Z"
      />
      <path
        className="fill-brand"
        d="M360.69,86.45v16.91h-8.65V57.58h37.52c2.6,0,4.81,.27,6.63,.81,1.82,.54,3.3,1.3,4.45,2.28,1.15,.98,1.98,2.14,2.5,3.48,.52,1.34,.78,2.84,.78,4.49v6.76c0,1.65-.26,3.14-.78,4.49-.52,1.35-1.36,2.5-2.5,3.48-1.15,.97-2.63,1.73-4.45,2.28-1.82,.54-4.03,.81-6.63,.81h-28.87Zm34.6-17.88c0-2.82-1.91-4.23-5.72-4.23h-28.87v15.35h28.87c3.82,0,5.72-1.41,5.72-4.23v-6.89Z"
      />
      <path
        className="fill-brand"
        d="M408.03,103.36V57.58h8.71v39.02h37.13v6.76h-45.85Z"
      />
      <path
        className="fill-brand"
        d="M472.15,103.36c-2.6,0-4.81-.27-6.63-.81-1.82-.54-3.31-1.3-4.45-2.28-1.15-.97-1.99-2.13-2.51-3.48-.52-1.34-.78-2.84-.78-4.49V57.58h8.65v34.79c0,2.82,1.91,4.23,5.72,4.23h28.42c3.81,0,5.72-1.41,5.72-4.23V57.58h8.65v34.73c0,1.65-.16,3.14-.49,4.49-.33,1.34-.96,2.5-1.89,3.48-.93,.98-2.21,1.73-3.84,2.28-1.62,.54-3.74,.81-6.34,.81h-30.24Z"
      />
      <path
        className="fill-brand"
        d="M521.83,103.36v-6.76h39.8c3.82,0,5.72-1.41,5.72-4.23v-5.79c0-2.82-1.91-4.23-5.72-4.23h-26.92c-2.6,0-4.81-.27-6.63-.81-1.82-.54-3.31-1.3-4.46-2.28s-1.98-2.13-2.5-3.48c-.52-1.34-.78-2.84-.78-4.49v-2.67c0-1.65,.26-3.14,.78-4.49,.52-1.34,1.35-2.5,2.5-3.48,1.15-.97,2.63-1.73,4.46-2.28,1.82-.54,4.03-.81,6.63-.81h38.76v6.76h-38.76c-3.81,0-5.72,1.41-5.72,4.23v2.8c0,2.82,1.91,4.23,5.72,4.23h26.92c2.6,0,4.81,.27,6.63,.81,1.82,.54,3.3,1.3,4.45,2.28,1.15,.98,1.98,2.14,2.5,3.48,.52,1.34,.78,2.84,.78,4.49v5.66c0,1.65-.26,3.14-.78,4.49-.52,1.34-1.35,2.5-2.5,3.48-1.15,.98-2.63,1.73-4.45,2.28-1.82,.54-4.03,.81-6.63,.81h-39.8Z"
      />
      <path
        className="fill-brand"
        d="M151.37,40.82c-3.87,0-5.8-1.17-5.8-3.51v-9.25c0-2.34,1.93-3.51,5.8-3.51h12.74v2.4h-12.74c-1.82,0-2.73,.37-2.73,1.11v9.25c0,.74,.91,1.11,2.73,1.11h7.19c1.82,0,2.73-.35,2.73-1.06v-5.41h3.07v5.41c0,2.31-1.93,3.47-5.8,3.47h-7.19Z"
      />
      <path
        className="fill-brand"
        d="M182.46,40.82V24.55h16.74v2.4h-13.66v4.07h12.97v2.4h-12.97v4.99h13.66v2.4h-16.74Z"
      />
      <path
        className="fill-brand"
        d="M217.27,40.82v-2.4h14.15c1.36,0,2.03-.5,2.03-1.5v-2.06c0-1-.68-1.5-2.03-1.5h-9.57c-.93,0-1.71-.1-2.36-.29-.65-.19-1.18-.46-1.58-.81-.41-.35-.7-.76-.89-1.24-.18-.48-.28-1.01-.28-1.59v-.95c0-.59,.09-1.12,.28-1.59,.19-.48,.48-.89,.89-1.24,.41-.35,.94-.62,1.58-.81,.65-.19,1.43-.29,2.36-.29h13.78v2.4h-13.78c-1.36,0-2.03,.5-2.03,1.5v.99c0,1,.68,1.5,2.03,1.5h9.57c.92,0,1.71,.1,2.36,.29,.65,.19,1.18,.46,1.58,.81,.41,.35,.71,.76,.89,1.24,.18,.48,.28,1.01,.28,1.59v2.01c0,.59-.09,1.12-.28,1.6-.18,.48-.48,.89-.89,1.24-.41,.35-.94,.62-1.58,.81-.65,.19-1.43,.29-2.36,.29h-14.15Z"
      />
      <path
        className="fill-brand"
        d="M261.33,40.82v-13.87h-8.53v-2.4h19.9v2.4h-8.3v13.87h-3.07Z"
      />
      <path
        className="fill-brand"
        d="M290.13,40.82V24.55h3.07v16.27h-3.07Z"
      />
      <path
        className="fill-brand"
        d="M316.57,40.82c-.92,0-1.71-.1-2.36-.29-.64-.19-1.17-.46-1.58-.81-.41-.35-.7-.76-.89-1.24-.18-.48-.28-1.01-.28-1.6v-8.41c0-.59,.09-1.12,.28-1.59,.19-.48,.48-.89,.89-1.24,.41-.35,.94-.62,1.58-.81,.65-.19,1.44-.29,2.36-.29h10.75c.93,0,1.71,.1,2.36,.29,.65,.19,1.18,.46,1.58,.81,.41,.35,.71,.76,.89,1.24,.18,.48,.28,1.01,.28,1.59v8.41c0,.59-.09,1.12-.28,1.6-.19,.48-.48,.89-.89,1.24-.41,.35-.94,.62-1.58,.81-.65,.19-1.43,.29-2.36,.29h-10.75Zm12.78-12.37c0-1-.68-1.5-2.03-1.5h-10.75c-1.35,0-2.03,.5-2.03,1.5v8.46c0,1,.68,1.5,2.03,1.5h10.75c1.36,0,2.03-.5,2.03-1.5v-8.46Z"
      />
      <path
        className="fill-brand"
        d="M370.61,40.82l-17.36-12.67v12.67h-2.68V24.55h2.66l16.2,12.09v-12.09h2.68v16.27h-1.5Z"
      />
    </svg>
  );
}
